import {
  ActionIcon,
  Button,
  Group,
  List,
  Paper,
  Progress,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconCircle, IconX } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { primaryColor } from '../../../../constants/theme';
import { ProfileResponseModel } from '../../../../types/Profile';
import { getProfileCompletion } from './profileCompletion';

const PROFILE_COMPLETION_BANNER_DISMISSED = 'profileCompletionBannerDismissed';
const EDIT_PROFILE_ROUTE = '/edit-profile';

export type ProfileCompletionBannerProps = {
  profile: ProfileResponseModel;
  isOwnProfile: boolean;
  onEditProfile?: () => void;
};

const isBannerDismissed = () =>
  sessionStorage.getItem(PROFILE_COMPLETION_BANNER_DISMISSED) === 'true';

export const ProfileCompletionBanner = ({
  profile,
  isOwnProfile,
  onEditProfile,
}: ProfileCompletionBannerProps) => {
  const navigate = useNavigate();
  const completion = useMemo(() => getProfileCompletion(profile), [profile]);
  const [dismissed, setDismissed] = useState(isBannerDismissed);

  const handleDismiss = () => {
    sessionStorage.setItem(PROFILE_COMPLETION_BANNER_DISMISSED, 'true');
    setDismissed(true);
  };

  const handleEditProfile = () => {
    if (onEditProfile) {
      onEditProfile();
      return;
    }

    navigate(EDIT_PROFILE_ROUTE);
  };

  if (!isOwnProfile || completion.isComplete || dismissed) {
    return null;
  }

  return (
    <Paper
      withBorder
      radius="md"
      bg="gray.0"
      p="md"
      w="100%"
      data-testid="profile-completion-banner"
    >
      <Stack gap="sm">
        <Group justify="space-between" align="start" wrap="nowrap">
          <Stack gap={2}>
            <Title order={4} fw="normal" ta="left">
              Complete Your Profile
            </Title>
            <Text c="dimmed" size="sm" ta="left">
              {completion.completionPercentage}% complete
            </Text>
          </Stack>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={handleDismiss}
            aria-label="Dismiss profile completion banner"
            data-testid="profile-completion-dismiss"
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>

        <Progress
          value={completion.completionPercentage}
          color={primaryColor}
          radius="xl"
          size="sm"
          role="progressbar"
          aria-valuenow={completion.completionPercentage}
          aria-valuemin={0}
          aria-valuemax={100}
        />

        <List spacing="sm" data-testid="profile-completion-missing-fields">
          {completion.missingFields.map((field) => (
            <List.Item
              key={field.key}
              icon={<IconCircle size={12} stroke={1.5} color={primaryColor} />}
            >
              <Stack gap={0} align="flex-start" w="100%">
                <Text fw={600} ta="left">
                  {field.label}
                </Text>
                <Text c="dimmed" size="sm" ta="left">
                  {field.benefit}
                </Text>
              </Stack>
            </List.Item>
          ))}
        </List>

        <Button
          fullWidth
          color={primaryColor}
          mt="xs"
          onClick={handleEditProfile}
          data-testid="profile-completion-edit-profile"
        >
          EDIT PROFILE
        </Button>
      </Stack>
    </Paper>
  );
};